import React, { useState, CSSProperties, ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import { createSvgIcon } from '@mui/material/utils';
import CircularProgress from '@mui/material/CircularProgress';

const BrokenImageIcon = createSvgIcon(
  <path d='M21 5v6.59l-2.29-2.3c-.39-.39-1.03-.39-1.42 0L14 12.59 10.71 9.3a.9959.9959 0 0 0-1.41 0L6 12.59 3 9.58V5c0-1.1.9-2 2-2h14c1.1 0 2 .9 2 2zm-3 6.42 3 3.01V19c0 1.1-.9 2-2 2H5c-1.1 0-2-.9-2-2v-6.58l2.29 2.29c.39.39 1.02.39 1.41 0l3.3-3.3 3.29 3.29c.39.39 1.02.39 1.41 0l3.3-3.28z' />,
  'BrokenImageIcon'
);

const Img = styled('img')({
  '@keyframes materialize': {
    '0%': {
      filter: 'saturate(20%) contrast(50%) brightness(120%)',
    },
    '75%': {
      filter: 'saturate(60%) contrast(100%) brightness(100%)',
    },
    '100%': {
      filter: 'saturate(100%) contrast(100%) brightness(100%)',
    },
  },
});

type Position = 'static' | 'relative' | 'absolute' | 'fixed' | 'sticky' | 'inherit' | 'initial' | 'revert' | 'unset';

type Fit = 'contain' | 'cover' | 'fill' | 'none' | 'scale-down' | 'inherit' | 'initial' | 'revert' | 'unset';

type Shift = false | null | 'top' | 'bottom' | 'left' | 'right';

interface ImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt' | 'height' | 'width' | 'onLoad' | 'onError'> {
  src: string;
  alt?: string;
  /**
   * Wrapper height (CSS value)
   */
  height?: number | string;
  /**
   * Wrapper width (CSS value)
   */
  width?: number | string;
  style?: CSSProperties;
  wrapperStyle?: CSSProperties;
  iconWrapperStyle?: CSSProperties;
  onLoad?: () => void;
  onError?: () => void;
  position?: Position;
  fit?: Fit;
  showLoading?: boolean | ReactNode;
  errorIcon?: boolean | ReactNode;
  shift?: Shift;
  distance?: number | string;
  shiftDuration?: number | null;
  bgColor?: string;
  duration?: number;
  easing?: string;
  className?: string;
  wrapperClassName?: string;
  iconWrapperClassName?: string;
}

export default function Image({
  src,
  style = {},
  wrapperStyle = {},
  iconWrapperStyle = {},
  onLoad: onLoadProp,
  onError: onErrorProp,
  alt = '',
  height = '100%',
  width = '100%',
  position = 'relative',
  fit = 'cover',
  showLoading = false,
  errorIcon = true,
  shift = false,
  distance = 100,
  shiftDuration = null,
  bgColor = 'inherit',
  duration = 3000,
  easing = 'cubic-bezier(0.7, 0, 0.6, 1)',
  className = '',
  wrapperClassName = '',
  iconWrapperClassName = '',
  ...rest
}: ImageProps): JSX.Element {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const handleLoad = (): void => {
    setLoaded(true);
    setError(false);
    if (onLoadProp) {
      onLoadProp();
    }
  };

  const handleError = (): void => {
    setError(true);
    setLoaded(false);
    if (onErrorProp) {
      onErrorProp();
    }
  };

  const shiftStyles: CSSProperties = {};
  if (shift) {
    // Only apply if shift is a non-false string
    shiftStyles[shift] = loaded ? 0 : distance;
  }

  const styles = {
    root: {
      width,
      height,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: bgColor,
      ...wrapperStyle,
    } as CSSProperties,
    image: {
      position,
      width: '100%',
      height: '100%',
      objectFit: fit,
      transitionProperty: `${shift ? `${shift}, ` : ''}opacity`,
      transitionDuration: `${shift ? `${shiftDuration ?? duration * 0.3}ms, ` : ''}${duration / 2}ms`,
      transitionTimingFunction: easing,
      opacity: loaded ? 1 : 0,
      animation: loaded ? `materialize ${duration}ms 1 ${easing}` : '',
      ...(shiftStyles as {}),
      ...style,
    } as CSSProperties,
    icons: {
      width: '100%',
      marginLeft: '-100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      opacity: loaded ? 0 : 1,
      ...iconWrapperStyle,
    } as CSSProperties,
  };

  const showErrorIconNode =
    typeof errorIcon !== 'boolean' && errorIcon ? (
      errorIcon
    ) : (
      <BrokenImageIcon style={{ fontSize: 56, color: '#bdbdbd' }} />
    );

  const loadingIndicatorNode =
    typeof showLoading !== 'boolean' && showLoading ? showLoading : <CircularProgress size={36} thickness={2} />;

  return (
    <div style={styles.root} className={`mui-image-wrapper ${wrapperClassName}`}>
      <Img
        src={src}
        alt={alt}
        style={styles.image}
        className={`mui-image-img ${className}`}
        onLoad={handleLoad}
        onError={handleError}
        {...rest}
      />
      {(Boolean(showLoading) || Boolean(errorIcon)) && (
        <div style={styles.icons} className={`mui-image-iconWrapper ${iconWrapperClassName}`}>
          {error && Boolean(errorIcon) && showErrorIconNode}
          {!error && !loaded && Boolean(showLoading) && loadingIndicatorNode}
        </div>
      )}
    </div>
  );
}
